/**
 * Chat Store - Centralized State Management for Stream Chat Integration
 *
 * This store manages all chat-related state using Zustand with selective subscriptions
 * for optimal performance. It serves as the single source of truth for:
 * - Stream Chat connection state
 * - Channel lists and active channel
 * - Message caching and pagination
 * - Real-time indicators (typing, presence)
 * - UI preferences and filters
 *
 * Performance optimizations:
 * - Selective subscriptions prevent unnecessary re-renders
 * - Direct action functions bypass context overhead
 * - Non-reactive getters for performance-critical operations
 * - Persistent storage for user preferences
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Channel, ChannelMemberResponse, Message } from 'stream-chat';
import type { ChannelType, ChatFilters, StreamChatUser } from '../types/chat.types';

// ============================================================================
// VERSION CONTROL
// ============================================================================

/**
 * Current version of the chat store schema
 * Increment when making breaking changes to the stored data structure
 */
const CHAT_STORE_VERSION = '1.0.0';

// ============================================================================
// TYPES
// ============================================================================

export interface ChatState {
  // Connection State
  isConnected: boolean;
  connectionError: string | null;
  reconnectAttempts: number;
  lastConnectedAt: Date | null;

  // User State
  currentUser: StreamChatUser | null;
  userToken: string | null;

  // Channel State
  activeChannelId: string | null;
  activeTab: ChannelType;
  channels: {
    move: Channel[];
    'dispatch-driver': Channel[];
    group: Channel[];
  };
  channelLoadingState: {
    move: boolean;
    'dispatch-driver': boolean;
    group: boolean;
  };

  // Move Context State
  currentMoveId: string | null;

  // Message State
  messageCache: Map<string, Message[]>; // channelId -> messages
  messageLoadingState: Map<string, boolean>; // channelId -> loading
  hasMoreMessages: Map<string, boolean>; // channelId -> hasMore

  // Real-time State
  typingUsers: Map<string, ChannelMemberResponse[]>; // channelId -> users
  onlineUsers: Set<string>; // userIds
  unreadCounts: Map<string, number>; // channelId -> count
  totalUnreadCount: number;

  // UI State
  isSidebarOpen: boolean;
  searchQuery: string;
  filters: ChatFilters;
  selectedMessageId: string | null;
  replyToMessage: Message | null;
  selectedChannelForInfo: string | null;

  // User Preferences (persisted)
  preferences: {
    notificationsEnabled: boolean;
    soundEnabled: boolean;
    enterToSend: boolean;
    compactMode: boolean;
    showTimestamps: boolean;
    messageGroupingInterval: number; // minutes
  };

  // Enhanced Notification Preferences (persisted)
  notificationPreferences: {
    enabled: boolean;
    respectRegionFilter: boolean; // Only notify for channels in selected regions
    channelTypes: {
      move: boolean;
      'dispatch-driver': boolean;
      group: boolean;
    };
    priorityOverrides: boolean; // Always notify for urgent/critical messages
    quietHours: {
      enabled: boolean;
      start: string; // "22:00" format
      end: string; // "08:00" format
      timezone?: string; // User's timezone
    };
    soundPreferences: {
      enabled: boolean;
      volume: number; // 0.0 to 1.0
      differentSoundsForTypes: boolean;
    };
  };

  // Performance Metrics
  renderStats: {
    lastRenderTime: number;
    messageRenderCount: number;
    channelSwitchTime: number;
  };
}

export interface ChatActions {
  // Connection Actions
  setConnectionState: (isConnected: boolean, error?: string) => void;
  incrementReconnectAttempts: () => void;
  resetReconnectAttempts: () => void;

  // User Actions
  setCurrentUser: (user: StreamChatUser | null, token?: string) => void;
  clearCurrentUser: () => void;

  // Channel Actions
  setActiveChannel: (channelId: string | null) => void;
  setActiveTab: (tab: ChannelType) => void;
  setChannels: (type: ChannelType, channels: Channel[]) => void;
  addChannel: (type: ChannelType, channel: Channel) => void;
  updateChannel: (channelId: string, updates: Partial<Channel>) => void;
  removeChannel: (type: ChannelType, channelId: string) => void;
  setChannelLoadingState: (type: ChannelType, loading: boolean) => void;

  // Message Actions
  setMessages: (channelId: string, messages: Message[]) => void;
  addMessage: (channelId: string, message: Message) => void;
  updateMessage: (channelId: string, messageId: string, updates: Partial<Message>) => void;
  removeMessage: (channelId: string, messageId: string) => void;
  prependMessages: (channelId: string, messages: Message[]) => void;
  setMessageLoadingState: (channelId: string, loading: boolean) => void;
  setHasMoreMessages: (channelId: string, hasMore: boolean) => void;
  clearMessageCache: (channelId?: string) => void;

  // Real-time Actions
  setTypingUsers: (channelId: string, users: ChannelMemberResponse[]) => void;
  addTypingUser: (channelId: string, user: ChannelMemberResponse) => void;
  removeTypingUser: (channelId: string, userId: string) => void;
  setUserOnline: (userId: string, online: boolean) => void;
  setUnreadCount: (channelId: string, count: number) => void;
  incrementUnreadCount: (channelId: string) => void;
  decrementUnreadCount: (channelId: string) => void;
  updateTotalUnreadCount: () => void;
  markChannelAsRead: (channelId: string) => void;

  // UI Actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setSearchQuery: (query: string) => void;
  setFilters: (filters: Partial<ChatFilters>) => void;
  clearFilters: () => void;
  setSelectedMessage: (messageId: string | null) => void;
  setReplyToMessage: (message: Message | null) => void;
  setSelectedChannelForInfo: (channelId: string | null) => void;

  // Preference Actions
  updatePreferences: (prefs: Partial<ChatState['preferences']>) => void;
  resetPreferences: () => void;

  // Enhanced Notification Preference Actions
  updateNotificationPreferences: (prefs: Partial<ChatState['notificationPreferences']>) => void;
  resetNotificationPreferences: () => void;
  toggleRegionNotifications: (enabled: boolean) => void;
  updateChannelTypeNotifications: (channelType: ChannelType, enabled: boolean) => void;
  updateQuietHours: (quietHours: Partial<ChatState['notificationPreferences']['quietHours']>) => void;

  // Move Context Actions
  setCurrentMove: (moveId: string | null) => void;

  // Utility Actions
  resetStore: () => void;
  clearCache: () => void;
  updateRenderStats: (stats: Partial<ChatState['renderStats']>) => void;
}

export type ChatStore = ChatState & ChatActions;

// ============================================================================
// DEFAULT VALUES
// ============================================================================

const defaultPreferences: ChatState['preferences'] = {
  notificationsEnabled: true,
  soundEnabled: true,
  enterToSend: true,
  compactMode: false,
  showTimestamps: true,
  messageGroupingInterval: 5,
};

const defaultNotificationPreferences: ChatState['notificationPreferences'] = {
  enabled: true,
  respectRegionFilter: true, // Default to respecting region filters
  channelTypes: {
    move: true,
    'dispatch-driver': true,
    group: true,
  },
  priorityOverrides: true, // Always allow critical messages through
  quietHours: {
    enabled: false,
    start: '22:00',
    end: '08:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  },
  soundPreferences: {
    enabled: true,
    volume: 0.3,
    differentSoundsForTypes: true,
  },
};

const initialState: ChatState = {
  // Connection State
  isConnected: false,
  connectionError: null,
  reconnectAttempts: 0,
  lastConnectedAt: null,

  // User State
  currentUser: null,
  userToken: null,

  // Channel State
  activeChannelId: null,
  activeTab: 'dispatch-driver',
  channels: {
    move: [],
    'dispatch-driver': [],
    group: [],
  },
  channelLoadingState: {
    move: false,
    'dispatch-driver': false,
    group: false,
  },

  // Move Context State
  currentMoveId: null,

  // Message State
  messageCache: new Map(),
  messageLoadingState: new Map(),
  hasMoreMessages: new Map(),

  // Real-time State
  typingUsers: new Map(),
  onlineUsers: new Set(),
  unreadCounts: new Map(),
  totalUnreadCount: 0,

  // UI State
  isSidebarOpen: true,
  searchQuery: '',
  filters: {},
  selectedMessageId: null,
  replyToMessage: null,
  selectedChannelForInfo: null,

  // User Preferences
  preferences: defaultPreferences,

  // Enhanced Notification Preferences
  notificationPreferences: defaultNotificationPreferences,

  // Performance Metrics
  renderStats: {
    lastRenderTime: 0,
    messageRenderCount: 0,
    channelSwitchTime: 0,
  },
};

// ============================================================================
// STORE CREATION
// ============================================================================

export const useChatStore = create<ChatStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Connection Actions
      setConnectionState: (isConnected, error) =>
        set({
          isConnected,
          connectionError: error || null,
          lastConnectedAt: isConnected ? new Date() : get().lastConnectedAt,
        }),

      incrementReconnectAttempts: () => set(state => ({ reconnectAttempts: state.reconnectAttempts + 1 })),

      resetReconnectAttempts: () => set({ reconnectAttempts: 0 }),

      // User Actions
      setCurrentUser: (user, token) =>
        set({
          currentUser: user,
          userToken: token || get().userToken,
        }),

      clearCurrentUser: () =>
        set({
          currentUser: null,
          userToken: null,
        }),

      // Channel Actions
      setActiveChannel: channelId => {
        const startTime = performance.now();
        set(state => ({
          activeChannelId: channelId,
          renderStats: {
            ...state.renderStats,
            channelSwitchTime: performance.now() - startTime,
          },
        }));
      },

      setActiveTab: tab => set({ activeTab: tab }),

      setChannels: (type, channels) =>
        set(state => ({
          channels: {
            ...state.channels,
            [type]: channels,
          },
        })),

      addChannel: (type, channel) =>
        set(state => ({
          channels: {
            ...state.channels,
            [type]: [...state.channels[type], channel],
          },
        })),

      updateChannel: (channelId, updates) =>
        set(state => {
          const newChannels = { ...state.channels };
          for (const type of ['move', 'dispatch-driver', 'group'] as ChannelType[]) {
            const index = newChannels[type].findIndex(ch => ch.id === channelId);
            if (index !== -1) {
              newChannels[type][index] = {
                ...newChannels[type][index],
                ...updates,
              } as any;
              break;
            }
          }
          return { channels: newChannels };
        }),

      removeChannel: (type, channelId) =>
        set(state => ({
          channels: {
            ...state.channels,
            [type]: state.channels[type].filter(ch => ch.id !== channelId),
          },
        })),

      setChannelLoadingState: (type, loading) =>
        set(state => ({
          channelLoadingState: {
            ...state.channelLoadingState,
            [type]: loading,
          },
        })),

      // Message Actions
      setMessages: (channelId, messages) =>
        set(state => {
          const newCache = new Map(state.messageCache);
          newCache.set(channelId, messages);
          return { messageCache: newCache };
        }),

      addMessage: (channelId, message) =>
        set(state => {
          const newCache = new Map(state.messageCache);
          const messages = newCache.get(channelId) || [];
          newCache.set(channelId, [...messages, message]);
          return { messageCache: newCache };
        }),

      updateMessage: (channelId, messageId, updates) =>
        set(state => {
          const newCache = new Map(state.messageCache);
          const messages = newCache.get(channelId);
          if (messages) {
            const index = messages.findIndex(m => m.id === messageId);
            if (index !== -1) {
              messages[index] = { ...messages[index], ...updates };
              newCache.set(channelId, [...messages]);
            }
          }
          return { messageCache: newCache };
        }),

      removeMessage: (channelId, messageId) =>
        set(state => {
          const newCache = new Map(state.messageCache);
          const messages = newCache.get(channelId);
          if (messages) {
            newCache.set(
              channelId,
              messages.filter(m => m.id !== messageId)
            );
          }
          return { messageCache: newCache };
        }),

      prependMessages: (channelId, messages) =>
        set(state => {
          const newCache = new Map(state.messageCache);
          const existingMessages = newCache.get(channelId) || [];
          newCache.set(channelId, [...messages, ...existingMessages]);
          return { messageCache: newCache };
        }),

      setMessageLoadingState: (channelId, loading) =>
        set(state => {
          const newLoadingState = new Map(state.messageLoadingState);
          newLoadingState.set(channelId, loading);
          return { messageLoadingState: newLoadingState };
        }),

      setHasMoreMessages: (channelId, hasMore) =>
        set(state => {
          const newHasMore = new Map(state.hasMoreMessages);
          newHasMore.set(channelId, hasMore);
          return { hasMoreMessages: newHasMore };
        }),

      clearMessageCache: channelId =>
        set(state => {
          const newCache = new Map(state.messageCache);
          if (channelId) {
            newCache.delete(channelId);
          } else {
            newCache.clear();
          }
          return { messageCache: newCache };
        }),

      // Real-time Actions
      setTypingUsers: (channelId, users) =>
        set(state => {
          const newTyping = new Map(state.typingUsers);
          newTyping.set(channelId, users);
          return { typingUsers: newTyping };
        }),

      addTypingUser: (channelId, user) =>
        set(state => {
          const newTyping = new Map(state.typingUsers);
          const users = newTyping.get(channelId) || [];
          if (!users.find(u => u.user_id === user.user_id)) {
            newTyping.set(channelId, [...users, user]);
          }
          return { typingUsers: newTyping };
        }),

      removeTypingUser: (channelId, userId) =>
        set(state => {
          const newTyping = new Map(state.typingUsers);
          const users = newTyping.get(channelId) || [];
          newTyping.set(
            channelId,
            users.filter(u => u.user_id !== userId)
          );
          return { typingUsers: newTyping };
        }),

      setUserOnline: (userId, online) =>
        set(state => {
          const newOnlineUsers = new Set(state.onlineUsers);
          if (online) {
            newOnlineUsers.add(userId);
          } else {
            newOnlineUsers.delete(userId);
          }
          return { onlineUsers: newOnlineUsers };
        }),

      setUnreadCount: (channelId, count) =>
        set(state => {
          const newUnreadCounts = new Map(state.unreadCounts);
          newUnreadCounts.set(channelId, count);
          return { unreadCounts: newUnreadCounts };
        }),

      incrementUnreadCount: channelId =>
        set(state => {
          const newUnreadCounts = new Map(state.unreadCounts);
          const current = newUnreadCounts.get(channelId) || 0;
          newUnreadCounts.set(channelId, current + 1);
          return { unreadCounts: newUnreadCounts };
        }),

      decrementUnreadCount: channelId =>
        set(state => {
          const newUnreadCounts = new Map(state.unreadCounts);
          const current = newUnreadCounts.get(channelId) || 0;
          newUnreadCounts.set(channelId, Math.max(0, current - 1));
          return { unreadCounts: newUnreadCounts };
        }),

      updateTotalUnreadCount: () =>
        set(state => {
          let total = 0;
          state.unreadCounts.forEach(count => {
            total += count;
          });
          return { totalUnreadCount: total };
        }),

      markChannelAsRead: channelId =>
        set(state => {
          const newUnreadCounts = new Map(state.unreadCounts);
          newUnreadCounts.set(channelId, 0);

          let total = 0;
          newUnreadCounts.forEach(count => {
            total += count;
          });

          return {
            unreadCounts: newUnreadCounts,
            totalUnreadCount: total,
          };
        }),

      // UI Actions
      toggleSidebar: () => set(state => ({ isSidebarOpen: !state.isSidebarOpen })),

      setSidebarOpen: open => set({ isSidebarOpen: open }),

      setSearchQuery: query => set({ searchQuery: query }),

      setFilters: filters =>
        set(state => {
          const newFilters = { ...state.filters, ...filters };
          let newActiveChannelId = state.activeChannelId;

          // Reset unread counts when regional filtering changes to avoid mismatches
          if (filters.regionIds && state.filters.regionIds !== filters.regionIds) {
            console.log('[ChatStore] Regional filter changed, resetting unread counts for consistency');
            // Clear all unread counts since we can't retroactively filter historical messages
            set(prevState => ({
              unreadCounts: new Map(),
              totalUnreadCount: 0,
            }));
          }

          // If regional filtering is being applied, check if active channel matches
          if (filters.regionIds && state.activeChannelId) {
            // Find the active channel across all channel lists
            let activeChannel: Channel | null = null;
            for (const channelList of Object.values(state.channels)) {
              const channel = channelList.find(ch => ch.id === state.activeChannelId);
              if (channel) {
                activeChannel = channel;
                break;
              }
            }

            // Check if active channel matches the new regional filter
            if (activeChannel) {
              const channelRegionId = (activeChannel as any).data?.region_id;
              const selectedRegionIds = filters.regionIds;

              console.log('[ChatStore] Checking active channel against regional filter:', {
                activeChannelId: state.activeChannelId,
                channelRegionId,
                selectedRegionIds,
                channelData: Object.keys((activeChannel as any).data || {}),
              });

              // If channel has a region_id and it's not in the selected regions, clear active channel
              if (channelRegionId && selectedRegionIds.length > 0 && !selectedRegionIds.includes(channelRegionId)) {
                newActiveChannelId = null;
                console.log('[ChatStore] Cleared active channel - does not match regional filter:', {
                  channelId: state.activeChannelId,
                  channelRegionId,
                  selectedRegionIds,
                });
              }
              // If no region filter is set (empty array), allow all channels
              else if (selectedRegionIds.length === 0) {
                console.log('[ChatStore] No regional filter applied, keeping active channel');
              }
              // Channel matches the regional filter
              else {
                console.log('[ChatStore] Active channel matches regional filter, keeping it selected');
              }
            }
          }

          console.log('[ChatStore] Active channel ID:', newActiveChannelId);

          return {
            filters: newFilters,
            activeChannelId: newActiveChannelId,
          };
        }),

      clearFilters: () => set({ filters: {} }),

      setSelectedMessage: messageId => set({ selectedMessageId: messageId }),

      setReplyToMessage: message => set({ replyToMessage: message }),

      setSelectedChannelForInfo: channelId => set({ selectedChannelForInfo: channelId }),

      // Preference Actions
      updatePreferences: prefs =>
        set(state => ({
          preferences: { ...state.preferences, ...prefs },
        })),

      resetPreferences: () => set({ preferences: defaultPreferences }),

      // Enhanced Notification Preference Actions
      updateNotificationPreferences: prefs =>
        set(state => ({
          notificationPreferences: { ...state.notificationPreferences, ...prefs },
        })),

      resetNotificationPreferences: () => set({ notificationPreferences: defaultNotificationPreferences }),

      toggleRegionNotifications: enabled =>
        set(state => ({
          notificationPreferences: {
            ...state.notificationPreferences,
            respectRegionFilter: enabled,
          },
        })),

      updateChannelTypeNotifications: (channelType, enabled) =>
        set(state => ({
          notificationPreferences: {
            ...state.notificationPreferences,
            channelTypes: {
              ...state.notificationPreferences.channelTypes,
              [channelType]: enabled,
            },
          },
        })),

      updateQuietHours: quietHours =>
        set(state => ({
          notificationPreferences: {
            ...state.notificationPreferences,
            quietHours: {
              ...state.notificationPreferences.quietHours,
              ...quietHours,
            },
          },
        })),

      // Move Context Actions
      setCurrentMove: moveId =>
        set({
          currentMoveId: moveId,
        }),

      // Utility Actions
      resetStore: () => set(initialState),

      clearCache: () =>
        set({
          messageCache: new Map(),
          messageLoadingState: new Map(),
          hasMoreMessages: new Map(),
          typingUsers: new Map(),
          unreadCounts: new Map(),
          totalUnreadCount: 0,
        }),

      updateRenderStats: stats =>
        set(state => ({
          renderStats: { ...state.renderStats, ...stats },
        })),
    }),
    {
      name: 'chat-store',
      version: 1,
      partialize: state => ({
        // Only persist user preferences and UI state
        preferences: state.preferences,
        notificationPreferences: state.notificationPreferences, // Persist enhanced notification prefs
        isSidebarOpen: state.isSidebarOpen,
        activeTab: state.activeTab,
        filters: state.filters, // Persist filters including regionIds
      }),
    }
  )
);

// ============================================================================
// SELECTOR HOOKS
// ============================================================================

// Connection selectors
export const useIsConnected = () => useChatStore(state => state.isConnected);
export const useConnectionError = () => useChatStore(state => state.connectionError);
export const useCurrentUser = () => useChatStore(state => state.currentUser);

// Channel selectors
export const useActiveChannel = () => {
  const activeChannelId = useChatStore(state => state.activeChannelId);
  const channels = useChatStore(state => state.channels);

  if (!activeChannelId) return null;

  for (const channelList of Object.values(channels)) {
    const channel = channelList.find(ch => ch.id === activeChannelId);
    if (channel) return channel;
  }

  return null;
};

export const useActiveTab = () => useChatStore(state => state.activeTab);
export const useChannels = (type: ChannelType) => useChatStore(state => state.channels[type]);
export const useAllChannels = () => useChatStore(state => state.channels);
export const useChannelLoadingState = (type: ChannelType) => useChatStore(state => state.channelLoadingState[type]);

// Message selectors
export const useMessages = (channelId: string) => useChatStore(state => state.messageCache.get(channelId) || []);
export const useMessageLoadingState = (channelId: string) =>
  useChatStore(state => state.messageLoadingState.get(channelId) || false);
export const useHasMoreMessages = (channelId: string) =>
  useChatStore(state => state.hasMoreMessages.get(channelId) ?? true);

// Real-time selectors
export const useTypingUsers = (channelId: string) => useChatStore(state => state.typingUsers.get(channelId) || []);
export const useIsUserOnline = (userId: string) => useChatStore(state => state.onlineUsers.has(userId));
export const useUnreadCount = (channelId?: string) => {
  if (channelId) {
    return useChatStore(state => state.unreadCounts.get(channelId) || 0);
  }
  return useChatStore(state => state.totalUnreadCount);
};

// UI selectors
export const useIsSidebarOpen = () => useChatStore(state => state.isSidebarOpen);
export const useSearchQuery = () => useChatStore(state => state.searchQuery);
export const useFilters = () => useChatStore(state => state.filters);
export const useSelectedMessage = () => useChatStore(state => state.selectedMessageId);
export const useReplyToMessage = () => useChatStore(state => state.replyToMessage);
export const useSelectedChannelForInfo = () => useChatStore(state => state.selectedChannelForInfo);

// Stats selectors
export const useOnlineUsersCount = () => useChatStore(state => state.onlineUsers.size);
export const useTotalUnreadCount = () => useChatStore(state => state.totalUnreadCount);
export const useWatchedRegionsCount = () => useChatStore(state => state.filters.regionIds?.length || 0);

// Preference selectors
export const usePreferences = () => useChatStore(state => state.preferences);
export const useNotificationsEnabled = () => useChatStore(state => state.preferences.notificationsEnabled);
export const useSoundEnabled = () => useChatStore(state => state.preferences.soundEnabled);

// Enhanced Notification Preference selectors
export const useNotificationPreferences = () => useChatStore(state => state.notificationPreferences);
export const useRegionNotificationsEnabled = () =>
  useChatStore(state => state.notificationPreferences.respectRegionFilter);
export const useChannelTypeNotifications = (channelType?: ChannelType) => {
  if (channelType) {
    return useChatStore(state => state.notificationPreferences.channelTypes[channelType]);
  }
  return useChatStore(state => state.notificationPreferences.channelTypes);
};
export const useQuietHours = () => useChatStore(state => state.notificationPreferences.quietHours);
export const usePriorityOverrides = () => useChatStore(state => state.notificationPreferences.priorityOverrides);
export const useSoundPreferences = () => useChatStore(state => state.notificationPreferences.soundPreferences);

// ============================================================================
// NON-REACTIVE GETTERS
// ============================================================================

export const getChatState = () => useChatStore.getState();
export const getActiveChannel = () => {
  const state = getChatState();
  if (!state.activeChannelId) return null;

  for (const channelList of Object.values(state.channels)) {
    const channel = channelList.find(ch => ch.id === state.activeChannelId);
    if (channel) return channel;
  }

  return null;
};
export const getMessages = (channelId: string) => getChatState().messageCache.get(channelId) || [];
export const getUnreadCount = (channelId?: string) => {
  const state = getChatState();
  return channelId ? state.unreadCounts.get(channelId) || 0 : state.totalUnreadCount;
};

// ============================================================================
// ACTION EXPORTS
// ============================================================================

// Export individual actions for use outside React components
export const {
  setConnectionState,
  setCurrentUser,
  setActiveChannel,
  setActiveTab,
  setChannels,
  addChannel,
  updateChannel,
  removeChannel,
  setMessages,
  addMessage,
  updateMessage,
  removeMessage,
  setTypingUsers,
  setUserOnline,
  setUnreadCount,
  markChannelAsRead,
  toggleSidebar,
  setSearchQuery,
  setFilters,
  setSelectedChannelForInfo,
  updatePreferences,
  resetStore,
  clearCache,
} = useChatStore.getState();
